import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from './api';

const GuestFileAccess = () => {
  const [searchParams] = useSearchParams();
  const [grantId] = useState(searchParams.get('grant_id'));
  const [token] = useState(searchParams.get('token'));
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [grant, setGrant] = useState(null);
  const [error, setError] = useState(null);
  const [showConsent, setShowConsent] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [viewerHtml, setViewerHtml] = useState(null);
  const [currentDocId, setCurrentDocId] = useState(null);

  useEffect(() => {
    if (!grantId || !token) {
      setError('Invalid access link. Please check your email for the correct link.');
    }
  }, [grantId, token]);

  const handleAccessGrant = async () => {
    if (!password) {
      alert('Please enter the grant password');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // First, verify grant exists and check consent requirements
      const grantRes = await api.get(`/api/file-grants/${grantId}/details`, {
        headers: {
          'X-Guest-Token': token
        }
      });

      setGrant(grantRes.data);

      // Check if consent is required
      if (grantRes.data.require_e_consent_for_guest && !consentAccepted) {
        setShowConsent(true);
        return;
      }

      // Try to open first document
      if (grantRes.data.documents && grantRes.data.documents.length > 0) {
        openDocument(grantRes.data.documents[0].id);
      }
    } catch (err) {
      console.error('Access error:', err);
      setError(err.response?.data?.detail || 'Failed to access grant. Please check your password and try again.');
    } finally {
      setLoading(false);
    }
  };

  const openDocument = async (docId) => {
    try {
      setLoading(true);
      
      const response = await api.post('/api/file-grants/open', {
        grant_id: grantId,
        doc_id: docId,
        password: password
      }, {
        headers: {
          'X-Guest-Token': token
        },
        responseType: 'text'
      });

      setViewerHtml(response.data);
      setCurrentDocId(docId);
    } catch (err) {
      console.error('Open error:', err);
      alert('Failed to open document: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleConsentAccept = () => {
    setConsentAccepted(true);
    setShowConsent(false);
    handleAccessGrant(); // Retry access after consent
  };

  if (showConsent) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full">
          <h2 className="text-2xl font-bold mb-4">Electronic Communications Consent Required</h2>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold mb-2">Consent to Receive Electronic Communications</h3>
            <div className="text-sm text-gray-700 space-y-2 max-h-96 overflow-y-auto">
              <p>By accessing these documents, you consent to receive documents and communications electronically.</p>
              <p><strong>Electronic Delivery:</strong> You agree that the HOA may provide notices, documents, and other communications to you electronically rather than in paper form.</p>
              <p><strong>Hardware and Software Requirements:</strong> To access and retain electronic communications, you will need a computer or device with internet access, a current web browser with cookies enabled, and sufficient storage space.</p>
              <p><strong>Withdrawal of Consent:</strong> You may withdraw your consent to receive electronic communications at any time by contacting the HOA board.</p>
              <p><strong>Paper Copies:</strong> You have the right to request paper copies of any electronic communications.</p>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold mb-2">Privacy Policy</h3>
            <div className="text-sm text-gray-700 space-y-2">
              <p>Your information will be kept confidential and used only for HOA management purposes.</p>
              <p>By proceeding, you acknowledge that you have read and agree to the HOA's privacy practices.</p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleConsentAccept}
              className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
            >
              ✓ I Accept
            </button>
            <button
              onClick={() => window.close()}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (viewerHtml) {
    return (
      <div className="h-screen flex flex-col">
        <div className="bg-gray-800 text-white p-4 flex justify-between items-center">
          <div>
            <h2 className="font-semibold">Secure Document Viewer</h2>
            <p className="text-sm text-gray-300">View-only access • Download disabled</p>
          </div>
          {grant && grant.documents && grant.documents.length > 1 && (
            <div className="flex gap-2">
              {grant.documents.map(doc => (
                <button
                  key={doc.id}
                  onClick={() => openDocument(doc.id)}
                  className={`px-3 py-1 rounded text-sm ${
                    currentDocId === doc.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  }`}
                >
                  {doc.title}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex-1">
          <iframe
            srcDoc={viewerHtml}
            className="w-full h-full border-0"
            sandbox="allow-same-origin allow-scripts"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Secure Document Access</h1>
          <p className="text-gray-600">Enter your password to view documents</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Grant Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAccessGrant()}
              placeholder="Enter password provided by HOA"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loading}
            />
          </div>

          <button
            onClick={handleAccessGrant}
            disabled={loading || !password}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition"
          >
            {loading ? 'Verifying...' : '🔓 Access Documents'}
          </button>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-start gap-2 text-sm text-gray-600">
            <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="font-medium">Secure Access</p>
              <p className="text-xs">This link is time-limited and password-protected. Documents are view-only with download disabled.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestFileAccess;
